import React, { useEffect } from 'react';
import '../legal/legal.css';
import { LangCode, useT } from '../i18n/I18nProvider';

// ─────────────────────────────────────────────────────────────────────────────
// WirDieBraunschweiger — public initiative page under "We The People".
// Main Events provides the platform, real-life gatherings are the product.
// Copy is local to this page because the concept is editorial and page-specific.
// ─────────────────────────────────────────────────────────────────────────────

const CITY_IMAGE = 'https://cdn.pixabay.com/photo/2020/02/03/13/49/braunschweig-4812936_1280.jpg';
const TABLE_IMAGE = 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?auto=format&fit=crop&w=1200&q=80';
const DANCE_IMAGE = 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1200&q=80';
const TALK_IMAGE = 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80';

const CONTACT = 'wirdiebraunschweiger@mainevents.seuytel.com';

type TextBlock = { title: string; text: string };
type PageCopy = {
    title: string;
    eyebrow: string;
    heroText: string;
    primaryCta: string;
    eventCta: string;
    ideaKicker: string;
    ideaTitle: string;
    ideaText: string;
    whyKicker: string;
    whyTitle: string;
    whyText: string;
    tableAlt: string;
    formatsKicker: string;
    formatsTitle: string;
    formats: TextBlock[];
    danceAlt: string;
    danceCaption: string;
    talkAlt: string;
    talkCaption: string;
    principlesKicker: string;
    principlesTitle: string;
    principles: string[];
    audiencesKicker: string;
    audiencesTitle: string;
    audiences: string[];
    hostsKicker: string;
    hostsTitle: string;
    hostsText: string;
    hostsCta: string;
    credit: string;
};

const COPY: Record<LangCode, PageCopy> = {
    de: {
        title: 'Wir die Braunschweiger',
        eyebrow: 'We The People · Braunschweig',
        heroText: 'Braunschweig vernetzt sich neu und zwar analog. Unsere Initiative bringt Menschen weg vom Smartphone und zurück in echte Räume: für echte Gespräche, gemeinsame Erlebnisse und persönliche Freundschaften.',
        primaryCta: 'Formate ansehen',
        eventCta: 'Event vorschlagen',
        ideaKicker: 'Die Idee',
        ideaTitle: 'Weg vom Bildschirm, rein ins Leben: Wo echte Nähe entsteht.',
        ideaText: 'Soziale Medien können echte menschliche Bindungen und emotionale Tiefe nicht ersetzen. Deshalb bringt „Wir die Braunschweiger“, die soziale Initiative unter dem Dach von Main Events, Menschen wieder im echten Räume zusammen. Wir schaffen den Sprung vom Smartphone in die Realität und holen das Gemeinschaftsgefühl zurück in die Stadt. Unsere Mission ist das bewusste persönliche Kennenlernen durch maßgeschneiderte Erlebnisse. Ob Dating, After-Work, Meet & Talk oder City-Touren: Wir veranstalten die Events, die aus Fremden echte Freunde machen.',
        whyKicker: 'Wo?',
        whyTitle: 'Digitale Welten zeigen uns alles. Erst reale Orte lassen uns fühlen.',
        whyText: 'Angenehme Orte beflügeln unsere Sinne und schenken uns die beste Atmosphäre für neue Kontakte. Braunschweig hat die Locations, die Kreativen und die Menschen mit Lust auf Leben, wir liefern den passenden Anlass. Regelmäßig und einladend schaffen wir Räume, die verbinden. Unsere Gastgeber vor Ort nehmen dir die Befangenheit und machen den Einstieg ganz leicht.',
        tableAlt: 'Braunschweiger sitzen gemeinsam an einem Tisch',
        formatsKicker: 'Event-Ideen',
        formatsTitle: 'Unsere Formate',
        formats: [
            { title: 'After Work', text: 'Wir bringen Menschen nach der Arbeit in den coolsten Bars und Restaurants zusammen. Kein Zwang, kein steifes Networking. Der perfekte Mix aus Entspannung, Spaß und neuen Kontakten.' },
            { title: 'Dating', text: 'Wir schaffen den perfekten Rahmen für Singles, die echte Begegnungen suchen. In sorgfältig ausgewählten Locations und einer entspannten Community-Atmosphäre wird das Kennenlernen leicht gemacht. Unsere Moderatoren begleiten das Event aufmerksam und helfen dabei, unbeschwert ins Gespräch zu kommen.' },
            { title: 'Dance, Party & cultural Nights', text: 'Erlebe unvergessliche Themenabende und Community-Nights in einer gepflegten und einladenden Atmosphäre. Wir bringen Menschen in ansprechenden, sauberen und sicheren Locations zusammen, die das Feiern angenehm machen.' },
            { title: 'Meet & Talk', text: 'Small conferences, Talks, Panels und offene Runden zu Stadt, Gründung, Kultur, Sport, Gesundheit und Zukunft. Wissen wird sozial, nicht nur frontal.' },
            { title: 'Stadt erleben', text: 'Spaziergänge, Games, Tastings, Kulturabende und lokale Entdeckungen, die Braunschweig nicht nur zeigen, sondern Braunschweiger dabei verbinden.' },
        ],
        danceAlt: 'Braunschweiger feiern bei einem Event',
        danceCaption: 'Partys und Social Nights mit bewusstem Community-Gefühl.',
        talkAlt: 'Braunschweiger diskutieren bei einem Treffen',
        talkCaption: 'Talks, Ideenabende und Konferenzen, bei denen Austausch im Mittelpunkt steht.',
        principlesKicker: 'Prinzipien',
        principlesTitle: 'Woran jedes Event gemessen wird',
        principles: [
            'Offline zuerst: Die App hilft beim Finden, Erinnern und Organisieren. Die eigentliche Magie passiert vor Ort.',
            'Sozialer Gewinn: Jedes Format muss neue Kontakte, Vertrauen oder echte gemeinsame Erinnerungen erzeugen.',
            'Niedrige Einstiegshürde: Auch Braunschweiger, die allein kommen, sollen sich willkommen und sicher fühlen.',
            'Lokale Partner: Bars, Vereine, Unternehmen, Kulturorte und Veranstalter bekommen ein sichtbares, gut kuratiertes Format.',
        ],
        audiencesKicker: 'Für wen?',
        audiencesTitle: 'Unsere Zielgruppe? Die Region Braunschweig',
        audiences: ['Neue Braunschweiger', 'Singles', 'Studierende', 'Berufstätige', 'Gründer & Kreative'],
        hostsKicker: 'Für Veranstalter',
        hostsTitle: 'Du hast einen Ort, ein Format oder eine Community?',
        hostsText: 'Wir helfen dabei, aus einer Idee ein klares Event zu machen: Besonders spannend sind Formate mit echtem sozialem Mehrwert.',
        hostsCta: 'Event gemeinsam starten',
        credit: 'Bildquellen: Braunschweig-Foto von Pixabay, Social- und Eventmotive von Unsplash.',
    },
    en: {
        title: 'We, the People of Braunschweig',
        eyebrow: 'We The People · Braunschweig',
        heroText: 'Braunschweig reconnects the analog way. Our initiative moves people away from their smartphones and back into real spaces: for real conversations, shared experiences, and personal friendships.',
        primaryCta: 'View formats',
        eventCta: 'Suggest an event',
        ideaKicker: 'The idea',
        ideaTitle: 'Away from the screen, into real life: Where true connection happens.',
        ideaText: 'Social media cannot replace true human bonds and emotional depth. That is why "Wir die Braunschweiger", the social initiative under the umbrella of Main Events, brings people back together in real spaces. We bridge the gap from the smartphone to reality and bring the sense of community back to the city. Our mission is to foster intentional in-person connections through tailored experiences. Whether it is dating, after-work parties, meet & talks, or city tours: we host the events that turn strangers into true friends.',
        whyKicker: 'Where?',
        whyTitle: 'Digital worlds show us everything. Only real places make us feel.',
        whyText: 'Pleasant spaces ignite our senses and create the perfect atmosphere for making new connections. Braunschweig has the locations, the creatives, and the people with a passion for life, we provide the right occasion. Regular and welcoming, we create spaces that connect. Our on-site hosts melt away any awkwardness and make taking that first step effortless.',
        tableAlt: 'Braunschweig residents sitting together at a table',
        formatsKicker: 'Event ideas',
        formatsTitle: 'Our formats',
        formats: [
            { title: 'After Work', text: 'We bring people together after work in the coolest bars and restaurants. No pressure, no stiff networking. The perfect mix of relaxation, fun, and new connections.' },
            { title: 'Dating', text: 'We create the perfect setting for singles looking for genuine connections. In carefully selected locations and a relaxed community atmosphere, getting to know each other is made easy. Our hosts attentively guide the event and help you strike up conversations effortlessly.' },
            { title: 'Dance, Party & cultural Nights', text: 'Experience unforgettable theme nights and community events in a refined and welcoming atmosphere. We bring people together in attractive, clean, and safe locations that make celebrating a pleasure.' },
            { title: 'Meet & Talk', text: 'Small conferences, Talks, panels and open circles about the city, founding, culture, sport, health and the future. Knowledge becomes social, not just frontal.' },
            { title: 'Experience the city', text: 'Walks, games, tastings, culture nights and local discoveries that do not just show Braunschweig but connect residents along the way.' },
        ],
        danceAlt: 'Braunschweig residents celebrating at an event',
        danceCaption: 'Parties and social nights with an intentional community feeling.',
        talkAlt: 'Braunschweig residents discussing at a gathering',
        talkCaption: 'Talks, idea nights and conferences where exchange is at the center.',
        principlesKicker: 'Principles',
        principlesTitle: 'How every event is measured',
        principles: [
            'Offline first: The app helps people find, remember and organize. The real magic happens on site.',
            'Social value: Every format must create new contacts, trust or shared memories.',
            'Low threshold: Braunschweig residents who come alone should feel welcome and safe.',
            'Local partners: Bars, clubs, companies, cultural venues and organizers get a visible, well-curated format.',
        ],
        audiencesKicker: 'For whom?',
        audiencesTitle: 'Our target audience? The Braunschweig region.',
        audiences: ['New Braunschweig residents', 'Singles', 'Students', 'Professionals', 'Founders & creatives'],
        hostsKicker: 'For organizers',
        hostsTitle: 'Do you have a venue, a format or a community?',
        hostsText: 'We help turn an idea into a clear event: Formats with real social value are especially interesting.',
        hostsCta: 'Start an event together',
        credit: 'Image sources: Braunschweig photo from Pixabay, social and event images from Unsplash.',
    },
    fr: {
        title: 'Nous, les Braunschweigois',
        eyebrow: 'We The People · Braunschweig',
        heroText: 'Braunschweig se reconnecte en mode analogue. Notre initiative sort les gens de leurs smartphones pour les ramener dans de vrais espaces : pour de vraies conversations, des expériences partagées et des amitiés sincères.',
        primaryCta: 'Voir les formats',
        eventCta: 'Proposer un événement',
        ideaKicker: 'L’idée',
        ideaTitle: 'Loin des écrans, place à la vraie vie : Là où naissent les vrais liens',
        ideaText: 'Les réseaux sociaux ne peuvent pas remplacer les vrais liens humains et la profondeur émotionnelle. C´est pourquoi « Wir die Braunschweiger », l´´nitiative sociale sous l´égide de Main Events, rassemble à nouveau les gens dans de vrais espaces. Nous faisons le pont entre le smartphone et la réalité pour ramener le sens de la communauté au cœur de la ville. Notre mission est de favoriser les rencontres personnelles et authentiques grâce à des expériences sur mesure. Qu´il s´agisse de speed dating, d´afterworks, de « meet & talks » ou de visites de la ville : nous organisons les événements qui transforment les inconnus en vrais amis.',
        whyKicker: 'Ou ?',
        whyTitle: 'Les mondes virtuels nous montrent tout. Seuls les lieux réels nous font vibrer.',
        whyText: 'Les lieux agréables éveillent nos sens et offrent la meilleure atmosphère pour tisser de nouveaux liens. Brunswick possède les lieux, les créatifs et les personnes passionnées par la vie, nous apportons le prétexte idéal pour se rencontrer. Réguliers et accueillants, nous créons des espaces qui unissent. Nos hôtes sur place effacent toute timidité et rendent le premier pas d´une facilité déconcertante.',
        tableAlt: 'Des Braunschweigois assis ensemble autour d’une table',
        formatsKicker: 'Idées d’événements',
        formatsTitle: 'Nos formats',
        formats: [
            { title: 'After Work', text: 'Nous rassemblons les gens après le travail dans les bars et restaurants les plus cools. Pas de pression, pas de networking rigide. Le mélange parfait entre détente, fun et nouvelles rencontres.' },
            { title: 'Dating', text: 'Nous créons le cadre idéal pour les célibataires à la recherche de vraies rencontres. Dans des lieux soigneusement sélectionnés et une atmosphère communautaire détendue, faire connaissance devient un jeu d´enfant. Nos animateurs accompagnent l´événement avec bienveillance et vous aident à engager la conversation en toute légèreté.' },
            { title: 'Dance, Party & cultural Nights', text: 'Vivez des soirées et des community nights inoubliables dans une atmosphère raffinée et chaleureuse. Nous rassemblons les gens dans des lieux attrayants, propres et sécurisés, où faire la fête devient un vrai plaisir.' },
            { title: 'Meet & Talk', text: 'Small conferences, Talks, panels et cercles ouverts sur la ville, l’entrepreneuriat, la culture, le sport, la santé et l’avenir. Le savoir devient social, pas seulement frontal.' },
            { title: 'Vivre la ville', text: 'Balades, jeux, dégustations, soirées culturelles et découvertes locales qui ne montrent pas seulement Braunschweig, mais relient les habitants en chemin.' },
        ],
        danceAlt: 'Des Braunschweigois font la fête lors d’un événement',
        danceCaption: 'Des fêtes et social nights avec un vrai sentiment de communauté.',
        talkAlt: 'Des Braunschweigois discutent lors d’une rencontre',
        talkCaption: 'Talks, soirées d’idées et conférences où l’échange est au centre.',
        principlesKicker: 'Principes',
        principlesTitle: 'Ce qui guide chaque événement',
        principles: [
            'Offline en priorité : l’app aide à trouver, mémoriser et organiser. La vraie magie se passe sur place.',
            'Valeur sociale : chaque format doit créer de nouveaux contacts, de la confiance ou des souvenirs partagés.',
            'Seuil d’entrée bas : les Braunschweigois qui viennent seuls doivent se sentir bienvenus et en sécurité.',
            'Partenaires locaux : bars, associations, entreprises, lieux culturels et organisateurs obtiennent un format visible et bien construit.',
        ],
        audiencesKicker: 'Pour qui ?',
        audiencesTitle: 'Notre public cible ? La région de Brunswick',
        audiences: ['Nouveaux Braunschweigois', 'Célibataires', 'Étudiants', 'Professionnels', 'Fondateurs & créatifs'],
        hostsKicker: 'Pour les organisateurs',
        hostsTitle: 'Vous avez un lieu, un format ou une communauté ?',
        hostsText: 'Nous aidons à transformer une idée en événement clair : Les formats avec une vraie valeur sociale sont particulièrement intéressants.',
        hostsCta: 'Lancer un événement ensemble',
        credit: 'Sources des images : photo de Braunschweig sur Pixabay, images sociales et événementielles sur Unsplash.',
    },
};

export default function WirDieBraunschweiger() {
    const { lang } = useT();
    const copy = COPY[lang] ?? COPY.de;

    useEffect(() => {
        const prevTitle = document.title;
        document.title = `${copy.title} – We The People`;
        return () => {
            document.title = prevTitle;
        };
    }, [copy.title]);

    return (
        <div className="se-wdb">
            <section className="se-wdb-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(255,249,232,0.95) 0%, rgba(255,249,232,0.86) 45%, rgba(255,196,0,0.2) 68%, rgba(18,24,38,0.18) 100%), url(${CITY_IMAGE})` }}>
                <div className="se-wdb-hero-inner">
                    <span className="se-wdb-eyebrow">{copy.eyebrow}</span>
                    <h1>{copy.title}</h1>
                    <p>{copy.heroText}</p>
                    <div className="se-wdb-cta-row">
                        <a className="se-wdb-cta" href="#formate">{copy.primaryCta}</a>
                        <a className="se-wdb-cta se-wdb-cta-light" href={`mailto:${CONTACT}`}>{copy.eventCta}</a>
                    </div>
                </div>
            </section>

            <main className="se-content se-wdb-content">
                <section className="se-wdb-intro">
                    <div>
                        <span className="se-wdb-kicker">{copy.ideaKicker}</span>
                        <h2>{copy.ideaTitle}</h2>
                    </div>
                    <p>{copy.ideaText}</p>
                </section>

                <section className="se-wdb-band">
                    <div className="se-wdb-band-text">
                        <span className="se-wdb-kicker">{copy.whyKicker}</span>
                        <h2>{copy.whyTitle}</h2>
                        <p>{copy.whyText}</p>
                    </div>
                    <img src={TABLE_IMAGE} alt={copy.tableAlt} loading="lazy" />
                </section>

                <section id="formate" className="se-wdb-section">
                    <div className="se-wdb-section-head">
                        <span className="se-wdb-kicker">{copy.formatsKicker}</span>
                        <h2>{copy.formatsTitle}</h2>
                    </div>
                    <div className="se-wdb-format-grid">
                        {copy.formats.map((format) => (
                            <article className="se-wdb-card" key={format.title}>
                                <h3>{format.title}</h3>
                                <p>{format.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="se-wdb-image-row">
                    <figure>
                        <img src={DANCE_IMAGE} alt={copy.danceAlt} loading="lazy" />
                        <figcaption>{copy.danceCaption}</figcaption>
                    </figure>
                    <figure>
                        <img src={TALK_IMAGE} alt={copy.talkAlt} loading="lazy" />
                        <figcaption>{copy.talkCaption}</figcaption>
                    </figure>
                </section>

                <section className="se-wdb-section">
                    <div className="se-wdb-section-head">
                        <span className="se-wdb-kicker">{copy.principlesKicker}</span>
                        <h2>{copy.principlesTitle}</h2>
                    </div>
                    <div className="se-wdb-principles">
                        {copy.principles.map((item) => (
                            <div className="se-wdb-principle" key={item}>{item}</div>
                        ))}
                    </div>
                </section>

                <section className="se-wdb-section">
                    <div className="se-wdb-section-head">
                        <span className="se-wdb-kicker">{copy.audiencesKicker}</span>
                        <h2>{copy.audiencesTitle}</h2>
                    </div>
                    <div className="se-wdb-audiences">
                        {copy.audiences.map((item) => <span key={item}>{item}</span>)}
                    </div>
                </section>

                <section className="se-wdb-hosts">
                    <div>
                        <span className="se-wdb-kicker">{copy.hostsKicker}</span>
                        <h2>{copy.hostsTitle}</h2>
                        <p>{copy.hostsText}</p>
                    </div>
                    <a className="se-wdb-cta se-wdb-hosts-cta" href={`mailto:${CONTACT}`}>{copy.hostsCta}</a>
                </section>

                <section className="se-wdb-credit">
                    <p>{copy.credit}</p>
                </section>
            </main>
        </div>
    );
}
